import React from 'react';
import { Field, useFormikContext } from 'formik';
import Select, { components, AriaOnFocus } from 'react-select';
import { useSearchResults } from '@context/DataContext';
import { isArray } from 'lodash';

import { bemClassName } from '@utils/bemClassName';
import { joinClassNames } from '@utils/joinClassNames';
import { useSearchFormContext } from '@context/SearchFormContext';
import FacetsApplyButton from '@atoms/FacetApplyButton/FacetsApplyButton';
import { ReactComponent as IconChevronUp } from '@assets/icons/chevron-up.svg?comp';
import { ReactComponent as IconTick } from '@assets/icons/tick.svg?comp';
import DateRangeField from '@atoms/DateRangeField/DateRangeField';
import ToggleButtonField from '@atoms/ToggleButtonField/ToggleButtonField';

import resolveFacetItems from '@templates/SearchForm/resolveFacetsItems';
import resolveStaticFacetItems from '@templates/SearchForm/resolveStaticFacetItems'; // eslint-disable-line

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <IconChevronUp className={bemClassName('react-select', 'dropdown-indicator-icon')} />
    </components.DropdownIndicator>
  );
};

const SelectFacet = ({ id, label, name, options }) => {
  const { initialValues, setFieldValue, values } = useFormikContext();

  const initialSelectedValue = initialValues[name];
  const initialSelectedIndex = options.findIndex((option) => option.value === initialSelectedValue);
  const defaultValue = initialSelectedIndex === -1 ? null : options[initialSelectedIndex];

  const selectedValue = values[name];
  const selectedIndex = options.findIndex((option) => option.value === selectedValue);
  const value = selectedIndex === -1 ? null : options[selectedIndex];

  const onFocusS = ({ focused, isDisabled }) => {
    return `Focused on option ${focused.label}${isDisabled ? ', disabled' : ''}`;
  };

  const handleSelectChange = (newValue, name) => {
    if (isArray(newValue)) {
      setFieldValue(
        name,
        newValue.map((item) => item.value)
      );
    } else if (!newValue || !('value' in newValue)) {
      setFieldValue(name, '');
    } else {
      setFieldValue(name, newValue.value);
    }
  };

  let columnClassName = `facet-filters__input facet-filters__input--select`;
  return (
    <div className={columnClassName}>
      <label htmlFor={id} className={joinClassNames(['facet-filters__label'])}>
        {label}
      </label>
      <Select
        aria-labelledby={label}
        ariaLiveMessages={{
          onFocusS,
        }}
        id={id}
        options={options}
        defaultValue={defaultValue}
        value={value}
        placeholder={`Select ${label.toLowerCase()}`}
        classNamePrefix="react-select"
        unstyled
        onChange={(selectedOption) => handleSelectChange(selectedOption, name)}
        components={{
          DropdownIndicator,
        }}
      />
    </div>
  );
};

const MultiSelectFacet = ({ id, label, name, options }) => {
  const onFocusMs = ({ focused, isDisabled }) => {
    return `Focused on option ${focused.label}${isDisabled ? ', disabled' : ''}`;
  };

  const { initialValues, setFieldValue, values } = useFormikContext();
  const initialSelectedValue = initialValues[name];
  const initialSelectedIndex = options.findIndex((option) => option.value === initialSelectedValue);
  const defaultValue = initialSelectedIndex === -1 ? null : [options[initialSelectedIndex]];

  const selectedValue = values[name];
  const value = selectedValue ? options.filter((option) => selectedValue.includes(option.value)) : [];

  const MultiValue = ({ index, getValue }) => {
    const overflow = getValue();
    return !index ? <div>{overflow.length} selected</div> : null;
  };

  return (
    <div className="facet-filters__input facet-filters__input--multiselect">
      <label htmlFor={id} className="facet-filters__label">
        {label}
      </label>
      <Select
        aria-labelledby={label}
        ariaLiveMessages={{
          onFocusMs,
        }}
        id={id}
        options={options}
        defaultValue={defaultValue}
        value={value}
        isMulti={true}
        isClearable={false}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        placeholder={`Select ${label.toLowerCase()}`}
        classNamePrefix="react-select"
        unstyled
        onChange={(selectedOption) =>
          setFieldValue(
            name,
            selectedOption.map((item) => item.value)
          )
        }
        formatOptionLabel={(data, formatOptionLabelMeta) => {
          // If is in menu list
          if (formatOptionLabelMeta.context === 'menu') {
            const isSelected = !!value.find((option) => option.id === data.id);
            // Check if is selected
            return (
              <>
                <span role="option" id={data.id} aria-labelledby={data.label} className={joinClassNames(['checkboxIconBg', 'checkbox-icon-bg', isSelected ? 'is-checked' : ''])}>
                  <IconTick className={joinClassNames([isSelected ? 'is-checked' : '', 'checkboxIcon'])} />
                </span>{' '}
                {data.label}
              </>
            );
          }
          return (
            <span aria-labelledby={data.label} role="option" id={data.id}>
              {data.label}
            </span>
          );
        }}
        components={{
          DropdownIndicator,
          MultiValueRemove: () => null,
          MultiValue,
        }}
      />
    </div>
  );
};

const CheckboxGroupOption = ({ id, label, name, value }) => {
  return (
    <div className="checkboxControlGroup">
      <Field type="checkbox" name={name} id={id} value={value} className={joinClassNames(['sqz-visuallyhidden', 'checkboxField'])} />
      <label htmlFor={id} className="checkboxLabel">
        <span className="checkboxIconBg">
          <IconTick className="checkboxIcon" />
        </span>
        <span className={joinClassNames(['checkboxText'])}>{label.trim()}</span>
      </label>
    </div>
  );
};

const CheckboxGroupFacet = ({ label, name, options }) => {
  return (
    <div className={`facet-filters__input facet-filters__input--group`}>
      <fieldset className="facet-filters__input--fieldset">
        <legend className="facet-filters__label">{label.trim()}</legend>
        {options.map((option) => (
          <CheckboxGroupOption key={option.id} {...option} name={name} />
        ))}
      </fieldset>
    </div>
  );
};

const HiddenField = ({ id, name, options }) => {
  return <Field type="hidden" name={name} id={id} className={'sqz-visuallyhidden'} value={options[0].value} />;
};

const renderFacetField = (facet) => {
  switch (facet.facetType) {
    case 'select':
      return <SelectFacet key={`facet-key-${facet.id}`} {...facet} />;
    case 'multiSelect':
      return <MultiSelectFacet key={`facet-key-${facet.id}`} {...facet} />;
    case 'checkboxGroup':
      return <CheckboxGroupFacet key={`facet-key-${facet.id}`} {...facet} />;
    case 'dateRange':
      return <DateRangeField key={`facet-key-${facet.id}`} {...facet} />;
    case 'toggle':
      return <ToggleButtonField key={`facet-key-${facet.id}`} {...facet} />;
    case 'hidden':
      return <HiddenField key={`facet-key-${facet.id}`} {...facet} />;
    default:
      return null;
  }
};

const Facets = ({ id }) => {
  const { query: _, ...facetItems } = useSearchFormContext();
  const { config, moduleType } = useSearchResults(); // eslint-disable-line

  // resolve FB facets
  let facetFields = resolveFacetItems(facetItems, config?.facetOverride);

  // inject any static facets / fields
  facetFields.push(...resolveStaticFacetItems(facetItems, moduleType));

  const optionalProps = {};
  if (id) {
    optionalProps.id = id;
  }

  return (
    <div {...optionalProps} className="facet-filters">
      <div className="facet-filters__inputs">{facetFields.map(renderFacetField)}</div>
      <div className="facet-filters__apply-button">
        <FacetsApplyButton />
      </div>
    </div>
  );
};

export default Facets;
